import React, { useRef } from "react";
import { Field, ErrorMessage, useFormikContext } from "formik";
import { Calendar, AlertCircle } from "lucide-react";

export const FilterFromToDateField = ({
  label = "Date Range",
  name = "",
  required = false,
  className = "",
  type = "date",
}) => {
  const { errors, touched } = useFormikContext();

  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  const fromName = `${name}.from`;
  const toName = `${name}.to`;

  const hasGroupError = Boolean(
    (touched?.[name]?.from && errors?.[name]?.from) ||
    (touched?.[name]?.to && errors?.[name]?.to),
  );

  const triggerPicker = (inputRef) => {
    if (inputRef.current) {
      if ("showPicker" in HTMLInputElement.prototype) {
        try {
          inputRef.current.showPicker();
        } catch (err) {
          inputRef.current.focus();
        }
      } else {
        inputRef.current.focus();
      }
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="flex flex-col gap-1.5 w-full">
        {/* Main Group Label */}
        {label && (
          <span
            className={`block text-xs font-semibold tracking-wide uppercase transition-colors ${
              hasGroupError ? "text-red-500" : "text-gray-600"
            }`}
          >
            {label}
            {required && <span className="ml-0.5 text-red-500">*</span>}
          </span>
        )}

        {/* Date Inputs Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          {/* FROM FIELD */}
          <Field name={fromName}>
            {({ field, meta }) => {
              const hasFieldError = Boolean(meta.touched && meta.error);

              return (
                <div className="flex flex-col gap-1 w-full">
                  <label
                    htmlFor={fromName}
                    className="text-[11px] font-medium text-gray-500 uppercase tracking-wider"
                  >
                    From
                  </label>
                  <div className="relative flex items-center w-full rounded-lg shadow-sm">
                    {/* Left Icon Button - Triggers Calendar Picker On First Click */}
                    <button
                      type="button"
                      onClick={() => triggerPicker(fromInputRef)}
                      className="absolute left-3 z-10 text-gray-400 hover:text-primary transition-colors focus:outline-none"
                    >
                      <Calendar className="h-4 w-4" />
                    </button>

                    {/* Native Date Input */}
                    <input
                      {...field}
                      ref={fromInputRef}
                      id={fromName}
                      type={type}
                      required={required}
                      className={`w-full rounded-lg border bg-white py-2 pl-9 pr-3 text-xs text-gray-900 transition-all duration-150 focus:outline-none 
                        [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0
                        ${
                          hasFieldError
                            ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                            : "border-gray-200 hover:border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                        }`}
                    />

                    {hasFieldError && (
                      <div className="absolute right-3 pointer-events-none text-red-500">
                        <AlertCircle className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </div>

                  <div className="min-h-[1rem] px-0.5">
                    <ErrorMessage name={fromName}>
                      {(msg) => (
                        <span className="text-[11px] font-medium text-red-500">
                          {msg}
                        </span>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
              );
            }}
          </Field>

          {/* TO FIELD */}
          <Field name={toName}>
            {({ field, meta }) => {
              const hasFieldError = Boolean(meta.touched && meta.error);

              return (
                <div className="flex flex-col gap-1 w-full">
                  <label
                    htmlFor={toName}
                    className="text-[11px] font-medium text-gray-500 uppercase tracking-wider"
                  >
                    To
                  </label>
                  <div className="relative flex items-center w-full rounded-lg shadow-sm">
                    {/* Left Icon Button - Triggers Calendar Picker On First Click */}
                    <button
                      type="button"
                      onClick={() => triggerPicker(toInputRef)}
                      className="absolute left-3 z-10 text-gray-400 hover:text-primary transition-colors focus:outline-none"
                    >
                      <Calendar className="h-4 w-4" />
                    </button>

                    {/* Native Date Input */}
                    <input
                      {...field}
                      ref={toInputRef}
                      id={toName}
                      type={type}
                      required={required}
                      className={`w-full rounded-lg border bg-white py-2 pl-9 pr-3 text-xs text-gray-900 transition-all duration-150 focus:outline-none 
                        [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0
                        ${
                          hasFieldError
                            ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                            : "border-gray-200 hover:border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                        }`}
                    />

                    {hasFieldError && (
                      <div className="absolute right-3 pointer-events-none text-red-500">
                        <AlertCircle className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </div>

                  <div className="min-h-[1rem] px-0.5">
                    <ErrorMessage name={toName}>
                      {(msg) => (
                        <span className="text-[11px] font-medium text-red-500">
                          {msg}
                        </span>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
              );
            }}
          </Field>
        </div>
      </div>
    </div>
  );
};

export default FilterFromToDateField;
